/**
 * Service for location custom sections (Phase 1: Local Government Admin custom content).
 * CRUD for sections and their key metrics; used by Admin Settings and public pages.
 */

import { supabaseClient } from './supabase';

export type CustomSectionPageId = 'transportation' | 'buildings' | 'people' | 'overview';

export interface CustomSectionMetric {
  id: string;
  section_id: string;
  label: string;
  value: string;
  display_order: number;
  color_hex: string | null;
}

export interface CustomSection {
  id: string;
  location_name: string;
  page_id: CustomSectionPageId;
  section_title: string;
  section_slug: string;
  content_html: string | null;
  overview_html: string | null;
  relevance_html: string | null;
  equity_html: string | null;
  methodology_html: string | null;
  overview_title: string | null;
  relevance_title: string | null;
  equity_title: string | null;
  methodology_title: string | null;
  image_url: string | null;
  link_url: string | null;
  link_label: string | null;
  display_order: number;
  is_published: boolean;
  created_by: string | null;
  updated_at: string;
  metrics?: CustomSectionMetric[];
}

export interface CustomSectionInsert {
  location_name: string;
  page_id: CustomSectionPageId;
  section_title: string;
  section_slug: string;
  content_html?: string | null;
  overview_html?: string | null;
  relevance_html?: string | null;
  equity_html?: string | null;
  methodology_html?: string | null;
  overview_title?: string | null;
  relevance_title?: string | null;
  equity_title?: string | null;
  methodology_title?: string | null;
  image_url?: string | null;
  link_url?: string | null;
  link_label?: string | null;
  display_order?: number;
  is_published?: boolean;
  created_by?: string | null;
}

export interface CustomSectionMetricInsert {
  section_id: string;
  label: string;
  value: string;
  display_order?: number;
  color_hex?: string | null;
}

/** Metric row as used in form (may omit section_id for new metrics). */
export interface CustomSectionMetricFormRow extends Omit<CustomSectionMetricInsert, 'section_id'> {
  section_id?: string;
}

const PAGE_IDS: CustomSectionPageId[] = ['transportation', 'buildings', 'people', 'overview'];

export function getAllowedPageIds(): CustomSectionPageId[] {
  return [...PAGE_IDS];
}

export function pageIdToLabel(pageId: CustomSectionPageId): string {
  const labels: Record<CustomSectionPageId, string> = {
    transportation: 'Transportation',
    buildings: 'Energy & Buildings',
    people: 'People & Demographics',
    overview: 'Overview'
  };
  return labels[pageId] ?? pageId;
}

/**
 * Fetch all published custom sections for a location and page, with metrics, ordered by display_order.
 */
export async function fetchCustomSections(
  locationName: string,
  pageId: CustomSectionPageId
): Promise<CustomSection[]> {
  const { data: sections, error: sectionsError } = await supabaseClient
    .from('location_custom_sections')
    .select('*')
    .eq('location_name', locationName)
    .eq('page_id', pageId)
    .eq('is_published', true)
    .order('display_order', { ascending: true });

  if (sectionsError) {
    console.error('Error fetching custom sections:', sectionsError);
    return [];
  }

  if (!sections?.length) return [];

  const sectionIds = sections.map((s) => s.id);
  const { data: metricsRows, error: metricsError } = await supabaseClient
    .from('location_custom_section_metrics')
    .select('*')
    .in('section_id', sectionIds)
    .order('display_order', { ascending: true });

  if (metricsError) {
    console.error('Error fetching custom section metrics:', metricsError);
  }

  const metricsBySection: Record<string, CustomSectionMetric[]> = {};
  (metricsRows || []).forEach((row) => {
    const list = metricsBySection[row.section_id] ?? [];
    list.push({
      id: row.id,
      section_id: row.section_id,
      label: row.label,
      value: row.value,
      display_order: row.display_order ?? 0,
      color_hex: row.color_hex ?? null
    });
    metricsBySection[row.section_id] = list;
  });

  return sections.map((s) => ({
    id: s.id,
    location_name: s.location_name,
    page_id: s.page_id as CustomSectionPageId,
    section_title: s.section_title,
    section_slug: s.section_slug,
    content_html: s.content_html ?? null,
    overview_html: (s as { overview_html?: string | null }).overview_html ?? null,
    relevance_html: (s as { relevance_html?: string | null }).relevance_html ?? null,
    equity_html: (s as { equity_html?: string | null }).equity_html ?? null,
    methodology_html: (s as { methodology_html?: string | null }).methodology_html ?? null,
    overview_title: (s as { overview_title?: string | null }).overview_title ?? null,
    relevance_title: (s as { relevance_title?: string | null }).relevance_title ?? null,
    equity_title: (s as { equity_title?: string | null }).equity_title ?? null,
    methodology_title: (s as { methodology_title?: string | null }).methodology_title ?? null,
    image_url: s.image_url ?? null,
    link_url: s.link_url ?? null,
    link_label: s.link_label ?? null,
    display_order: s.display_order ?? 0,
    is_published: !!s.is_published,
    created_by: s.created_by ?? null,
    updated_at: s.updated_at,
    metrics: metricsBySection[s.id] ?? []
  }));
}

/**
 * Fetch all custom sections for a location (all pages). For admin UI.
 */
export async function fetchAllCustomSectionsForLocation(
  locationName: string
): Promise<CustomSection[]> {
  const { data: sections, error } = await supabaseClient
    .from('location_custom_sections')
    .select('*')
    .eq('location_name', locationName)
    .order('page_id')
    .order('display_order', { ascending: true });

  if (error) {
    console.error('Error fetching all custom sections:', error);
    return [];
  }

  if (!sections?.length) return [];

  const sectionIds = sections.map((s) => s.id);
  const { data: metricsRows } = await supabaseClient
    .from('location_custom_section_metrics')
    .select('*')
    .in('section_id', sectionIds)
    .order('display_order', { ascending: true });

  const metricsBySection: Record<string, CustomSectionMetric[]> = {};
  (metricsRows || []).forEach((row) => {
    const list = metricsBySection[row.section_id] ?? [];
    list.push({
      id: row.id,
      section_id: row.section_id,
      label: row.label,
      value: row.value,
      display_order: row.display_order ?? 0,
      color_hex: row.color_hex ?? null
    });
    metricsBySection[row.section_id] = list;
  });

  return sections.map((s) => ({
    id: s.id,
    location_name: s.location_name,
    page_id: s.page_id as CustomSectionPageId,
    section_title: s.section_title,
    section_slug: s.section_slug,
    content_html: s.content_html ?? null,
    overview_html: (s as { overview_html?: string | null }).overview_html ?? null,
    relevance_html: (s as { relevance_html?: string | null }).relevance_html ?? null,
    equity_html: (s as { equity_html?: string | null }).equity_html ?? null,
    methodology_html: (s as { methodology_html?: string | null }).methodology_html ?? null,
    overview_title: (s as { overview_title?: string | null }).overview_title ?? null,
    relevance_title: (s as { relevance_title?: string | null }).relevance_title ?? null,
    equity_title: (s as { equity_title?: string | null }).equity_title ?? null,
    methodology_title: (s as { methodology_title?: string | null }).methodology_title ?? null,
    image_url: s.image_url ?? null,
    link_url: s.link_url ?? null,
    link_label: s.link_label ?? null,
    display_order: s.display_order ?? 0,
    is_published: !!s.is_published,
    created_by: s.created_by ?? null,
    updated_at: s.updated_at,
    metrics: metricsBySection[s.id] ?? []
  }));
}

/**
 * Create a custom section and optionally metrics. Returns the created section with metrics.
 */
export async function createCustomSection(
  payload: CustomSectionInsert,
  metrics: CustomSectionMetricInsert[],
  userId: string
): Promise<CustomSection | null> {
  const { data: section, error: sectionError } = await supabaseClient
    .from('location_custom_sections')
    .insert({
      ...payload,
      created_by: userId,
      updated_at: new Date().toISOString()
    })
    .select()
    .single();

  if (sectionError || !section) {
    console.error('Error creating custom section:', sectionError);
    return null;
  }

  if (metrics.length > 0) {
    const metricsPayload = metrics.map((m, i) => ({
      section_id: section.id,
      label: m.label,
      value: m.value,
      display_order: m.display_order ?? i,
      color_hex: m.color_hex ?? null
    }));
    await supabaseClient.from('location_custom_section_metrics').insert(metricsPayload);
  }

  return fetchSectionById(section.id);
}

/**
 * Update a custom section and replace its metrics.
 */
export async function updateCustomSection(
  sectionId: string,
  updates: Partial<Omit<CustomSectionInsert, 'location_name'>>,
  metrics: CustomSectionMetricInsert[],
  userId: string
): Promise<CustomSection | null> {
  const { error: updateError } = await supabaseClient
    .from('location_custom_sections')
    .update({
      ...updates,
      updated_at: new Date().toISOString()
    })
    .eq('id', sectionId);

  if (updateError) {
    console.error('Error updating custom section:', updateError);
    return null;
  }

  await supabaseClient.from('location_custom_section_metrics').delete().eq('section_id', sectionId);

  if (metrics.length > 0) {
    const metricsPayload = metrics.map((m, i) => ({
      section_id: sectionId,
      label: m.label,
      value: m.value,
      display_order: m.display_order ?? i,
      color_hex: m.color_hex ?? null
    }));
    await supabaseClient.from('location_custom_section_metrics').insert(metricsPayload);
  }

  return fetchSectionById(sectionId);
}

/**
 * Delete a custom section (cascade deletes metrics).
 */
export async function deleteCustomSection(sectionId: string): Promise<boolean> {
  const { error } = await supabaseClient
    .from('location_custom_sections')
    .delete()
    .eq('id', sectionId);

  if (error) {
    console.error('Error deleting custom section:', error);
    return false;
  }
  return true;
}

async function fetchSectionById(sectionId: string): Promise<CustomSection | null> {
  const { data: section, error } = await supabaseClient
    .from('location_custom_sections')
    .select('*')
    .eq('id', sectionId)
    .single();

  if (error || !section) return null;

  const { data: metricsRows } = await supabaseClient
    .from('location_custom_section_metrics')
    .select('*')
    .eq('section_id', sectionId)
    .order('display_order', { ascending: true });

  const metrics: CustomSectionMetric[] = (metricsRows || []).map((row) => ({
    id: row.id,
    section_id: row.section_id,
    label: row.label,
    value: row.value,
    display_order: row.display_order ?? 0,
    color_hex: row.color_hex ?? null
  }));

  const s = section as Record<string, unknown>;
  return {
    id: section.id,
    location_name: section.location_name,
    page_id: section.page_id as CustomSectionPageId,
    section_title: section.section_title,
    section_slug: section.section_slug,
    content_html: section.content_html ?? null,
    overview_html: (s.overview_html as string | null) ?? null,
    relevance_html: (s.relevance_html as string | null) ?? null,
    equity_html: (s.equity_html as string | null) ?? null,
    methodology_html: (s.methodology_html as string | null) ?? null,
    overview_title: (s.overview_title as string | null) ?? null,
    relevance_title: (s.relevance_title as string | null) ?? null,
    equity_title: (s.equity_title as string | null) ?? null,
    methodology_title: (s.methodology_title as string | null) ?? null,
    image_url: section.image_url ?? null,
    link_url: section.link_url ?? null,
    link_label: section.link_label ?? null,
    display_order: section.display_order ?? 0,
    is_published: !!section.is_published,
    created_by: section.created_by ?? null,
    updated_at: section.updated_at,
    metrics
  };
}

/** Generate a URL-safe slug from a title. */
export function slugFromTitle(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}
